/**
 * One row on `/invites`. Presentation only: takes props, makes no API call,
 * owns no state. The whole card except the two action buttons activates
 * `onJoin`, and a rejected card is inert (R3, R4).
 */
import { Button, Card, Skeleton } from "@sportsmet/ui";
import type { ReactNode } from "react";

import { PoolCardContent, poolCardStyle } from "../pools/PoolCardContent";
import {
  LINK_ROW_NOT_NOW_LABEL,
  type typeInviteCardModel,
} from "./invitesListState";

export type typeInviteCardProps = {
  model: typeInviteCardModel;
  /** True while this card's decline mutation is in flight. Disables both actions. */
  isRejecting: boolean;
  onJoin: (inviteId: string) => void;
  onReject: (inviteId: string) => void;
};

export const InviteCard = (props: typeInviteCardProps): ReactNode => {
  const { model } = props;

  const rejected = model.phase === "rejected";
  const contest = model.facts[1] ?? "";
  return (
    <div
      data-testid={`invite-card-${model.inviteId}`}
      aria-label={model.poolName}
      onClick={
        rejected || props.isRejecting
          ? undefined
          : () => {
              props.onJoin(model.inviteId);
            }
      }
    >
      <Card padding="default" tone="default" style={poolCardStyle(contest)}>
        <PoolCardContent
          name={model.poolName}
          imageUrl={model.bannerImageUrl}
          contestType={contest}
          sport={model.facts[0]}
          date={model.sentAtLabel}
          facts={model.facts}
          byline={
            model.creatorHandle
              ? `@${model.creatorHandle} · ${model.inviterLine}`
              : model.inviterLine
          }
        >
          {rejected ? (
            <p
              className="mt-3 text-body text-ink-secondary"
              data-testid={`invite-rejected-${model.inviteId}`}
            >
              {model.rejectedMessage}
            </p>
          ) : (
            <div className="pool-card-actions">
              <Button
                className="pool-card-primary"
                disabled={props.isRejecting}
                onClick={(event) => {
                  event.stopPropagation();
                  props.onJoin(model.inviteId);
                }}
              >
                Join
              </Button>
              <Button
                variant="outline"
                loading={props.isRejecting}
                loadingLabel={model.isFromLink ? "Removing" : "Rejecting"}
                onClick={(event) => {
                  event.stopPropagation();
                  props.onReject(model.inviteId);
                }}
              >
                {model.isFromLink ? LINK_ROW_NOT_NOW_LABEL : "Reject"}
              </Button>
            </div>
          )}
        </PoolCardContent>
      </Card>
    </div>
  );
};

export const InviteCardSkeleton = (): ReactNode => (
  <Skeleton shape="card" label="Loading your invites" />
);
